import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { HeightService } from '../height/height.service';
import { PanelStylesService } from '../panel-styles/panel-styles.service';
import { MaterialService } from '../material/material.service';
import { WorksurfaceService } from '../worksurface/worksurface.service';

import { Height } from '../height/height';
import { PanelStyles } from '../panel-styles/panel-styles';
import { Material } from '../material/material';
import { Worksurface } from '../worksurface/worksurface';

@Component({
  selector: 'app-selections-accordion',
  imports: [TitleCasePipe, Height, PanelStyles, Material, Worksurface],
  templateUrl: './selections-accordion.html',
  styleUrl: './selections-accordion.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectionsAccordion {
  private http = inject(HttpClient);

  private heightService = inject(HeightService);
  private panelStylesService = inject(PanelStylesService);
  protected materialService = inject(MaterialService);
  protected worksurfaceService = inject(WorksurfaceService);

  protected selectedHeight = computed(() => this.heightService.selectedHeight());
  protected selectedPanelStyle = computed(() => this.panelStylesService.selectedStyle());
  protected selectedWorksurface = computed(() => this.worksurfaceService.selectedWorksurface());

  readonly zones = computed(() => {
    const height = this.heightService.selectedHeight();
    const style = this.panelStylesService.selectedStyle();

    let result = ['T3', 'T2', 'T1', 'T0'];

    if (height == 32) {
      result = result.filter(tab => tab !== 'T3');
      result = result.filter(tab => tab !== 'T2');
      if (style?.endsWith('B')) result = result.filter(tab => tab !== 'T1');
    } else if (height === 39) {
      result = result.filter(tab => tab !== 'T3');
      if (style?.endsWith('B') || style?.endsWith('D')) result = result.filter(tab => tab !== 'T2');
      if (style?.endsWith('C') || style?.endsWith('D')) result = result.filter(tab => tab !== 'T1');
    } else if (height === 52 || height === 65) {
      if (style?.endsWith('B') || style?.endsWith('D') || style?.endsWith('F') || style?.endsWith('H')) {
        result = result.filter(tab => tab !== 'T3');
      }
      if (style?.endsWith('C') || style?.endsWith('D') || style?.endsWith('G') || style?.endsWith('H')) {
        result = result.filter(tab => tab !== 'T2');
      }
      if (style?.endsWith('E') || style?.endsWith('F') || style?.endsWith('G') || style?.endsWith('H')) {
        result = result.filter(tab => tab !== 'T1');
      }
    }

    return result;
  });

  protected selections = computed(() =>
    this.zones().map(zone => ({
      zone,
      ...this.materialService.getSelection(zone)()
    }))
  );

  protected getColorLabel = this.materialService.getColorLabel.bind(this.materialService);

  toggleAccordion(index: number): void {
    (window as any).toggleAccordion(index);
  }

  // ---------------------------------------------------------------------------
  // NEW: Export the panel by compositing the stacked PNG <img> layers onto a
  // normal <canvas> (no html2canvas).
  //
  // Assumptions (based on your DOM):
  // - #panel_image contains a ".png-stack" div with multiple <img> layers.
  // - CSS positions those layers to overlap (absolute/relative).
  // - Images are local (same-origin) like "images/panel_resources/...".
  // ---------------------------------------------------------------------------

  private async loadImage(src: string): Promise<HTMLImageElement> {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // harmless for same-origin; helpful if you later use CORS
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  }

  private getNumericPx(value: string | null | undefined): number {
    if (!value) return 0;
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }

  private async ensureAllDomImagesLoaded(root: HTMLElement): Promise<void> {
    const domImgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
    await Promise.all(
      domImgs.map(async (img) => {
        try {
          await (img as any).decode?.();
        } catch {
          if (!img.complete) {
            await new Promise<void>((res) => {
              img.onload = () => res();
              img.onerror = () => res();
            });
          }
        }
      })
    );
  }

  async downloadPanel(): Promise<void> {
    const panel = document.getElementById('panel_image');
    if (!(panel instanceof HTMLElement)) {
      console.error('panel_image not found / not HTMLElement');
      return;
    }

    // Find your stacked layers container (matches your provided HTML)
    const stack = panel.querySelector('.png-stack') as HTMLElement | null;
    if (!stack) {
      console.error('Cannot find .png-stack inside #panel_image');
      return;
    }

    // Make sure any already-rendered <img> tags are loaded
    await this.ensureAllDomImagesLoaded(stack);

    const layerImgs = Array.from(stack.querySelectorAll('img')) as HTMLImageElement[];
    if (!layerImgs.length) {
      console.error('No <img> layers found in .png-stack');
      return;
    }

    // Determine export size:
    // Prefer the first layer's *natural* size (typical for sprites/layers),
    // fall back to the rendered stack size.
    const first = layerImgs[0];
    const exportWidth =
      (first.naturalWidth && first.naturalWidth > 0) ? first.naturalWidth : Math.round(stack.getBoundingClientRect().width);
    const exportHeight =
      (first.naturalHeight && first.naturalHeight > 0) ? first.naturalHeight : Math.round(stack.getBoundingClientRect().height);

    const canvas = document.createElement('canvas');
    canvas.width = exportWidth;
    canvas.height = exportHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Canvas 2D context not available');
      return;
    }

    // Transparent background (matches your "no background color" preference)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw layers in DOM order (your stack HTML order is the layer order)
    // We respect CSS sizing/positioning if your layers are not exactly 1:1 by:
    // - reading computed width/height
    // - reading computed translate via getBoundingClientRect relative to stack
    const stackRect = stack.getBoundingClientRect();

    for (const domImg of layerImgs) {
      const src = domImg.currentSrc || domImg.src;
      if (!src) continue;

      // Load a fresh Image object for reliable drawImage (esp. if browser caching quirks)
      let img: HTMLImageElement;
      try {
        img = await this.loadImage(src);
      } catch (e) {
        console.warn(e);
        continue;
      }

      const imgRect = domImg.getBoundingClientRect();
      const computed = window.getComputedStyle(domImg);

      // Position relative to stack in rendered pixels
      const xPx = imgRect.left - stackRect.left;
      const yPx = imgRect.top - stackRect.top;

      // Size in rendered pixels (prefer rect)
      const wPx = imgRect.width;
      const hPx = imgRect.height;

      // If stack is scaled in CSS, map rendered pixels -> canvas pixels.
      // scaleX = canvasWidth / renderedWidth
      const renderedW = stackRect.width || canvas.width;
      const renderedH = stackRect.height || canvas.height;

      const scaleX = canvas.width / renderedW;
      const scaleY = canvas.height / renderedH;

      const x = xPx * scaleX;
      const y = yPx * scaleY;
      const w = wPx * scaleX;
      const h = hPx * scaleY;

      // Respect opacity
      const opacity = Number.parseFloat(computed.opacity || '1');
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = Number.isFinite(opacity) ? opacity : 1;

      // Draw
      ctx.drawImage(img, x, y, w, h);

      ctx.globalAlpha = prevAlpha;
    }

    const link = document.createElement('a');
    link.download = 'panel_screenshot.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  hideSuccess() {
    document.getElementById('email_good')?.classList.add('hidden');
  }

  emailSelection() {
    console.log("Email Button Clicked!");
    (document.getElementById('send_email') as HTMLButtonElement).disabled = true;
    (document.getElementById('send_email') as HTMLButtonElement).textContent = 'Sending...';
    let domain = window.location.hostname;
    const first_name = (document.getElementById('first-name') as HTMLInputElement).value;
    const last_name = (document.getElementById('last-name') as HTMLInputElement).value;
    const email = (document.getElementById('email') as HTMLInputElement).value;
    const height = (document.getElementById('height') as HTMLInputElement).value;
    const style = (document.getElementById('style') as HTMLInputElement).value;
    const worksurface = (document.getElementById('worksurface') as HTMLInputElement).value;

    let t4 = "";
    if (document.getElementById('T4')) t4 = (document.getElementById('T4') as HTMLInputElement).value;
    else t4 = "NA";

    let t3 = "";
    if (document.getElementById('T3')) t3 = (document.getElementById('T3') as HTMLInputElement).value;
    else t3 = "NA";

    let t2 = "";
    if (document.getElementById('T2')) t2 = (document.getElementById('T2') as HTMLInputElement).value;
    else t2 = "NA";

    let t1 = "";
    if (document.getElementById('T1')) t1 = (document.getElementById('T1') as HTMLInputElement).value;
    else t1 = "NA";

    let t0 = "";
    if (document.getElementById('T0')) t0 = (document.getElementById('T0') as HTMLInputElement).value;
    else t0 = "NA";

    console.log(first_name, last_name, email);
    console.log(t4);
    console.log(t3);
    console.log(t2);
    console.log(t1);
    console.log(t0);

    const data = {
      domain: domain,
      first_name: first_name,
      last_name: last_name,
      email: email,
      worksurface: worksurface,
      height: height,
      style: style,
      t4: t4,
      t3: t3,
      t2: t2,
      t1: t1,
      t0: t0,
    };

    this.http.post('https://' + domain + '/send_email.php', data)
      .subscribe(res => {
        console.log(res);
        document.getElementById('email_good')?.classList.remove('hidden');
        (document.getElementById('send_email') as HTMLButtonElement).disabled = false;
        (document.getElementById('send_email') as HTMLButtonElement).textContent = 'Send';
      });
  }
}
